<script setup lang="ts">
import type { Component, HTMLAttributes } from 'vue';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import HeaderInfoTooltip from '@/components/workspace/HeaderInfoTooltip.vue';
import { cn } from '@/lib/utils';

const props = withDefaults(defineProps<{
    eyebrow?: string;
    title: string;
    description?: string;
    icon?: Component | null;
    class?: HTMLAttributes['class'];
    headerClass?: HTMLAttributes['class'];
    contentClass?: HTMLAttributes['class'];
    density?: 'default' | 'compact';
    presentation?: 'default' | 'editorial';
    headerTone?: 'default' | 'highlight';
    contentLayout?: 'padded' | 'flush';
}>(), {
    eyebrow: '',
    description: '',
    icon: null,
    class: '',
    headerClass: '',
    contentClass: '',
    density: 'default',
    presentation: 'default',
    headerTone: 'default',
    contentLayout: 'padded',
});

const isEditorial = computed(() => props.presentation === 'editorial');
const isHighlightedHeader = computed(() => props.headerTone === 'highlight');
const isFlushContent = computed(() => props.contentLayout === 'flush');
</script>

<template>
    <Card :class="cn(
        'section-surface min-w-0 max-w-full gap-0 !py-0 shadow-none',
        isEditorial && 'section-surface--editorial',
        (isHighlightedHeader || isFlushContent) && 'overflow-hidden',
        props.class,
    )">
        <CardHeader :class="cn(
            'min-w-0',
            isEditorial ? 'space-y-2 border-b border-border/55 px-5 py-4' : 'space-y-3',
            !isEditorial && (density === 'compact' ? 'px-5 pb-0 pt-5' : 'px-6 py-5'),
            isHighlightedHeader && 'border-border/70 bg-muted/45',
            props.headerClass,
        )">
            <div class="min-w-0">
                <div class="flex min-w-0 items-center gap-2">
                    <span
                        v-if="icon"
                        :class="cn(
                            'section-surface__icon inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary',
                            isHighlightedHeader && 'bg-background/75 text-foreground',
                        )"
                    >
                        <slot name="icon">
                            <component :is="icon" class="size-3.5" />
                        </slot>
                    </span>
                    <CardTitle :class="cn(
                        density === 'compact' || isEditorial ? 'text-base font-semibold tracking-tight' : 'text-lg font-semibold tracking-tight',
                        'min-w-0 break-words text-foreground',
                    )">{{ title }}</CardTitle>
                    <HeaderInfoTooltip
                        v-if="description"
                        :description="description"
                        :label="`About ${title}`"
                    />
                </div>
            </div>

            <CardAction v-if="$slots.actions" class="min-w-0 max-w-full self-start">
                <slot name="actions" />
            </CardAction>
        </CardHeader>

        <CardContent :class="cn(
            'min-w-0 max-w-full',
            isFlushContent
                ? '!px-0 pb-0'
                : isEditorial
                    ? 'py-[var(--app-card-padding-y)]'
                    : (density === 'compact' ? 'px-5 pb-5 pt-4' : 'px-6 pb-6'),
            props.contentClass,
        )">
            <slot />
        </CardContent>
    </Card>
</template>
